import React, { useEffect, useState } from "react";
import { MessageSquareText, MessageCircle, Mail, Smartphone } from "lucide-react";
import { API, fmtDate } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const ICON = { whatsapp: MessageCircle, email: Mail, sms: Smartphone };
const STATUS_CLS = { sent: "bg-emerald-100 text-emerald-800", shared: "bg-sky-100 text-sky-800", failed: "bg-red-100 text-red-700", mock_sent: "bg-slate-100 text-slate-600" };

export function MessageList({ rows, compact = false, testid = "message-list" }) {
  const { t } = useLang();
  if (!rows?.length) return <p className="text-sm text-slate-500" data-testid={`${testid}-empty`}>{t("msgs.empty")}</p>;
  return (
    <div className={`divide-y divide-slate-100 ${compact ? "" : "bg-white rounded-xl border border-slate-200"}`} data-testid={testid}>
      {rows.map((m) => {
        const Icon = ICON[m.channel] || MessageSquareText;
        return (
          <div key={m.id} className="p-3 flex gap-3 text-sm" data-testid={`message-row-${m.id.slice(0, 8)}`}>
            <Icon className="h-4 w-4 mt-0.5 text-slate-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                {!compact && <span className="font-medium">{m.customer_name}</span>}
                <span className="font-mono text-xs text-slate-500">{m.loan_number}</span>
                <span className="text-xs text-slate-500">{m.phone || m.email}</span>
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATUS_CLS[m.status] || "bg-slate-100 text-slate-600"}`} data-testid={`message-status-${m.id.slice(0, 8)}`}>{t(`msgs.status_${m.status}`) || m.status}</span>
                {m.kind && m.kind !== "reminder" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-700">{m.kind.replace("notice:", "")}</span>}
                <span className="ml-auto text-xs text-slate-400">{fmtDate(m.created_at)} {m.created_at?.slice(11, 16)} · {m.created_by}</span>
              </div>
              <p className="text-xs text-slate-600 mt-1 whitespace-pre-line line-clamp-3">{m.message}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Messages() {
  const { t } = useLang();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [channel, setChannel] = useState("all");
  const [status, setStatus] = useState("all");
  useEffect(() => {
    const params = {};
    if (q) params.q = q;
    if (channel !== "all") params.channel = channel;
    if (status !== "all") params.status = status;
    API.get("/shop/messages", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [q, channel, status]);
  return (
    <div className="space-y-6" data-testid="messages-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 flex items-center gap-3"><MessageSquareText className="h-7 w-7 text-emerald-600" />{t("msgs.title")}</h1>
        <div className="flex-1" />
        <Input data-testid="messages-search" placeholder={t("msgs.search_ph")} value={q} onChange={(e) => setQ(e.target.value)} className="w-56 bg-white" />
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger data-testid="messages-channel-filter" className="w-36 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">{t("flt.all")}</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem><SelectItem value="sms">SMS</SelectItem><SelectItem value="email">Email</SelectItem></SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="messages-status-filter" className="w-36 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{["all", "sent", "shared", "failed", "mock_sent"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? t("flt.all") : t(`msgs.status_${s}`)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <p className="text-sm text-slate-500">{t("msgs.desc")}</p>
      {rows && <MessageList rows={rows} />}
    </div>
  );
}
